<template>
  <module-page v-bind="pageParams" :customBreadcrumbs="customBreadcrumbs">
    <v-layout row wrap>
      <v-flex xs12 md8 lg8>
        <v-tabs class="page__tabs" hide-slider v-model="selectedTab">
          <v-tab
            v-for="(tab, i) in pageParams.tabs"
            :to="`${tab.value}`"
            :key="i"
            active-class="support--background white--text"
          >
            {{
              tab.title.mobile
                ? mobile
                  ? tab.title.mobile
                  : tab.title.notMobile
                : tab.title
            }}
          </v-tab>
        </v-tabs>
        <v-btn
          class="ticket-details__goback-btn support--color"
          @click="goBack"
        >
          <i class="bexsicon-left" />
          {{ this.$t('actions.goBack') }}
        </v-btn>

        <screen-loading v-if="interactionListLoading"></screen-loading>

        <div v-else>
          <ticket-card
            class="ticket-details__info"
            for-details
            v-if="ticket"
            :statusParams="ticketStatus"
            :item="ticket"
          ></ticket-card>
          <div v-for="(item, i) in publicComments" :key="i" class="mb-3 pr-3">
            <div class="ticket-interaction__date">
              {{ formatDateTime(item.dateCreation) }}
            </div>
            <v-card>
              <v-card-title class="ticket-interaction__title">
                <b>
                  {{ formatRequesterName(item.person.name) }}
                  {{ getInteractionDefinition(item.person.type, i) }}
                </b>
              </v-card-title>
              <v-card-text
                v-html="item.publicComment.contentHtml"
                class="ticket-interaction__description"
              />
              <v-card-actions>
                <file-preview
                  show-download
                  @download="downloadFile"
                  :items="item.attachments"
                  fileProp="url"
                  titleProp="name"
                  btn-classes="support--background white--text"
                ></file-preview>
              </v-card-actions>
            </v-card>
          </div>

          <v-form
            v-model="formValid"
            @submit.prevent="interact"
            ref="interactionForm"
            v-if="allowInteraction && isOwner"
            lazy-validation
          >
            <v-card class="mt-2 mr-3 mb-5">
              <v-card-text>
                <v-layout row wrap>
                  <v-flex xs12>
                    <label required class="v-label --required__label">
                      {{ this.$t('pages.support.messageInput') }}
                    </label>
                    <div
                      class="ticket__input"
                      :class="{ ticket__input__error: !formValid }"
                    >
                      <v-textarea
                        required
                        auto-grow
                        solo
                        v-model="interaction.content"
                        :rules="rule.required"
                      ></v-textarea>
                      <file-input
                        @input="updateFiles"
                        @fileToLarge="handleFileToLarge"
                        @fileInvalid="handleFileInvalid"
                        :maxFileSize="maxFileSize"
                        :label="$t('labels.fileInput.title')"
                        :btn-label="$t('labels.fileInput.selectBtn')"
                        :drag-and-drop-label="
                          $t('labels.fileInput.dragAndDropAction')
                        "
                        class="ticket__file-input"
                        btn-class="support--background white--text btn-md"
                        drag-and-drop-class="support--color btn-md"
                        append-icon="attachment"
                        loadColor="blue"
                      ></file-input>
                    </div>
                  </v-flex>
                  <v-flex xs12>
                    <span class="error--text">
                      {{ fileError }}
                    </span>
                    <file-preview
                      show-remove
                      :items="files"
                      btn-classes="support--background white--text btn-md"
                      @remove="removeFile"
                    ></file-preview>
                  </v-flex>
                </v-layout>
              </v-card-text>
              <v-card-actions>
                <v-btn dark class="support--background btn-md" type="submit">
                  <v-progress-circular
                    color="white"
                    indeterminate
                    v-if="interactionFormLoading"
                  ></v-progress-circular>
                  <span v-else>
                    {{ this.$t('actions.send') }}
                  </span>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
          <empty-state-card v-else icon="chat">
            <div v-if="!allowInteraction" slot="message">
              <p class="empty-state__text">
                {{
                  this.$t('pages.support.ticketDetails.solicitationCloseWhen')
                }}
              </p>
              <p class="empty-state__date">
                {{ formatDateTime(ticket.doneDate) }}
              </p>
              <v-btn
                dark
                @click="$router.push({ name: 'ticketForm' })"
                class="empty-state__btn"
              >
                {{ this.$t('pages.support.ticketDetails.openSolicitation') }}
              </v-btn>
            </div>
            <div v-else-if="!isOwner" slot="message">
              <p class="empty-state__text">
                {{
                  this.$t('pages.support.ticketDetails.interactionNotAllowed')
                }}
              </p>
              <v-btn
                dark
                @click="$router.push({ name: 'ticketForm' })"
                class="empty-state__btn"
              >
                {{ this.$t('pages.support.ticketDetails.openSolicitation') }}
              </v-btn>
            </div>
          </empty-state-card>
        </div>
      </v-flex>
      <v-flex xs12 md4 lg4 class="mt-4 mb-4">
        <faq-card
          :items="faqItems"
          :loading="faqLoading"
          :error="faqError"
          :params="faqCardParams"
          :errorText="$t('responses.error')"
          @faqSelected="goToFaq"
          :retryFunction="getFaqs"
        ></faq-card>
      </v-flex>
    </v-layout>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '@/utils/enums';
import { APP, USER } from '@/utils/constants';
import notificationTypes from '@/utils/notificationTypes';
import faqMixin from '@/mixins/modules/faq';
import supportMixin from '@/mixins/modules/support';
import upload from '@/services/upload';

import ModulePage from '@/components/layout/Page';
import EmptyStateCard from '@/components/layout/EmptyStateCard';
import FilePreview from '@/components/layout/FilePreview';
import FaqCard from '@/components/modules/FaqCard';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import TicketCard from '@/components/support/TicketCard';
import TextArea from '@/components/fields/TextArea';
import FileInput from '@/components/fields/FileInput';
import getCurrentBandwidth from '@/utils/bandwidth';

export default {
  name: 'ticket-form',
  mixins: [faqMixin, supportMixin],
  components: {
    ModulePage,
    FaqCard,
    ScreenLoading,
    TicketCard,
    TextArea,
    EmptyStateCard,
    FileInput,
    FilePreview,
  },
  computed: {
    customBreadcrumbs() {
      return [
        {
          text: this.$t('pages.support.ticketDetails.breadcrumbLv1'),
        },
        {
          text: this.$t('pages.support.ticketDetails.breadcrumbLv2', {
            ticketNumber: this.ticket.number,
          }),
        },
      ];
    },
    publicComments() {
      return this.ticket.interactions.filter((o) => o.publicComment);
    },
  },
  data() {
    return {
      ticket: {
        number: null,
        interactions: [],
      },
      interaction: {
        content: '',
        attachments: [],
      },
      files: [],
      fileError: '',
      selectedTab: '',
      isOwner: true,
      formValid: false,
      allowInteraction: true,
      interactionListLoading: false,
      interactionFormLoading: false,
      ticketStatus: enums.ticketStatus,
      pageParams: this.$store.getters[APP.PAGES].support,
      faqCardParams: this.$store.getters[APP.APP_MODULES].faq,
      maxFileSize: enums.maxUploadFileSize,
    };
  },
  created() {
    bus.$on('unit:change', this.goToList);

    const { number } = this.$router.currentRoute.params;

    if (number) {
      this.ticket.number = number;
      this.createTrace(enums.logTraceActions.ticketDetails, { number });
    }

    this.getFaqs();
    this._getTicket();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.goToList);
  },
  methods: {
    getInteractionDefinition(type, index) {
      const { customer, agent, description, message } = this.$t('labels');
      const isCustomer = type === 2; // TODO: octadesk backend service should send this information!
      const isFirstOne = index === 0;

      return `${isCustomer ? customer : agent} | ${
        isFirstOne ? description : message
      }`;
    },
    handleFileToLarge() {
      this.fileError = this.$t('responses.filesToLarge');
    },
    handleFileInvalid(file) {
      this.fileError = this.$t('responses.fileInvalid', { file });
    },
    goToList(unitObj) {
      this.$router.push({ name: 'ticketList' });
    },
    updateFiles(v) {
      this.fileError = '';
      if (v) this.files.push(...v);
    },
    removeFile(v) {
      this.fileError = '';
      this.files.splice(this.files.indexOf(v), 1);
    },
    downloadFile(v) {
      window.open(v.url, '_blank');
    },
    formatRequesterName(v) {
      const names = v.split(' ');
      if (names.length > 1) {
        return `${names[0]} ${names[names.length - 1]}`.toLowerCase();
      } else {
        return names[0].toLowerCase();
      }
    },
    saveFiles() {
      const files = this.files.map((o) => o.binary);
      return upload(files, enums.fileCategory.TICKET_ATTACHMENT);
    },
    async interact() {
      if (!this.$refs.interactionForm.validate()) return;
      let traceData = {};
      try {
        this.interactionFormLoading = true;
        const { number } = this.ticket;
        let { content, attachments } = this.interaction;
        traceData = {
          content,
          number,
          summary: this.ticket.summary,
          octadeskNumberUrl: this.ticket.octadeskNumberUrl,
        };

        if (this.files.length > 0) {
          const files = await this.saveFiles();
          if (files) attachments = files;
        }

        traceData.attachments = attachments;

        const payload = {
          comments: {
            public: { content, attachments },
          },
        };

        const updatedTicket = await this.ticketInteract(number, payload);

        this.createTrace(enums.logTraceActions.ticketInteraction, traceData);
        bus.$emit('snackbar:open', {
          text: this.$t('responses.ticketDetailsSuccess'),
          color: enums.snackbarColors.success,
        });

        const lastInteraction = updatedTicket.lastHumanInteraction;

        lastInteraction.publicComment = lastInteraction.comments.find(
          (comment) => comment.isPublic
        );
        this.ticket.interactions.push(lastInteraction);
        this.interaction.content = '';
        this.files = [];
        this.$refs.interactionForm.resetValidation();
      } catch (err) {
        traceData.error = {
          message: err.message,
          stack: err.stack,
        };
        const bandwidthInMbps = await getCurrentBandwidth();
        traceData.connectionSpeed = `${bandwidthInMbps} MB`;
        this.createTrace(
          enums.logTraceActions.ticketInteractionError,
          traceData
        );

        const already2interactionsErro =
          err.data.message === 'already2interactions';
        const text = this.$t(
          already2interactionsErro
            ? 'responses.already2interactionsWithThisContent'
            : 'responses.error'
        );
        const color = already2interactionsErro ? 'warning' : 'error';

        bus.$emit('snackbar:open', {
          text,
          color: enums.snackbarColors[color],
        });
      } finally {
        this.interactionFormLoading = false;
      }
    },
    async _getTicket() {
      try {
        this.interactionListLoading = true;
        this.ticket = await this.getTicket(this.ticket.number);

        this.allowInteraction =
          enums.ticketStatus[this.ticket.currentStatusName].allowInteraction;
        this.isOwner = this.ticket.requesterCustomField ?
          this.ticket.requesterCustomField.cpf__cnpj == this.user.username : this.ticket.requesterMail == this.user.email;
      } catch (err) {
        this.isOwner = false;
      } finally {
        this.interactionListLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/breakpoint';

.ticket-details {
  &__info {
    margin-left: 0 !important;
  }

  &__goback-btn {
    font-size: 0.8em;
    height: 25px;
    margin-left: 0;

    i {
      font-size: 0.75em;
      padding-right: 5px;
    }
  }
}

.ticket-interaction {
  &__date {
    font-size: 0.8em;
    padding: 0.5em 0;
    color: var(--label-color);
  }

  &__title {
    font-weight: 500;
    background-color: #cae4f39e;
    padding: 10px 16px;
    text-transform: capitalize;
    color: var(--light-dark-color);
  }

  &__description {
    overflow-wrap: break-word;
  }
}

.empty-state {
  &__text {
    margin: 0;
  }

  &__date {
    margin: 0;
    font-weight: var(--font-weight);
  }

  &__btn {
    color: #fff;
    background-color: var(--support-color) !important;
    margin: 0.3em 0;
    height: 27px;

    @include breakpoint(mobile) {
      padding: 7px !important;
    }
  }
}
.ticket {
  &__input {
    border: 1px lightgray solid;
    border-radius: 5px;

    padding: 0px 0px 5px;

    &__error {
      border: 1px red solid;
    }

    >>> .v-input__slot {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.02),
        0 1px 5px 0 rgba(0, 0, 0, 0.02) !important;

      textarea {
        margin: 0 !important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/support/ticket/TicketDetails.vue